import addLife, { createLifePack } from "@rpldy/life-events";
import { invariant, logger, triggerCancellable, devFreeze, merge, clone } from "@rpldy/shared";
import getAbortEnhancer from "@rpldy/abort";
import getProcessor from "./processor";
import { UPLOADER_EVENTS } from "./consts";
import { getMandatoryOptions, deepProxyUnwrap } from "./utils";
import composeEnhancers from "./composeEnhancers";
const EVENT_NAMES = Object.values(UPLOADER_EVENTS);
const EXT_OUTSIDE_ENHANCER_TIME = "Uploady - uploader extensions can only be registered by enhancers",
  EXT_ALREADY_EXISTS = "Uploady - uploader extension by this name [%s] already exists";
let counter = 0;
const getComposedEnhancer = extEnhancer => composeEnhancers(getAbortEnhancer(), extEnhancer);
const getEnhancedUploader = (uploader, options, triggerWithUnwrap, setEnhancerTime) => {
  //TODO: create raw-uploader without internal enhancers (while default-uploader will use abort & xhr-sender enhancers)

  //TODO need new mechanism for registering and using internal methods (abort, send)
  //that will use enhancers but also allow overrides without having to expose the method in the options (ie: send)
  const enhancer = options.enhancer ? getComposedEnhancer(options.enhancer) : getAbortEnhancer();
  setEnhancerTime(true);
  const enhanced = enhancer(uploader, triggerWithUnwrap);
  setEnhancerTime(false);

  //graceful handling for enhancer forgetting to return uploader
  return enhanced || uploader;
};
const createUploader = options => {
  counter += 1;
  const uploaderId = `uploader-${counter}`;
  let enhancerTime = false;
  const extensions = {};
  logger.debugLog(`uploady.uploader: creating new instance (${uploaderId})`, {
    options,
    counter
  });
  let uploaderOptions = getMandatoryOptions(options);
  const update = updateOptions => {
    //TODO: updating concurrent and maxConcurrent means we need to update the processor - not supported yet!
    uploaderOptions = merge({}, uploaderOptions, updateOptions); //need deep merge for destination
    return uploader;
  };
  const add = (files, addOptions) => {
    const processOptions = merge({}, uploaderOptions, addOptions);
    if (processOptions.clearPendingOnAdd) {
      clearPending();
    }
    return processor.addNewBatch(files, processOptions).then(() => {
      logger.debugLog(`uploady.uploader: finished adding file data to be processed`);
    });
  };
  const clearPending = () => {
    processor.clearPendingBatches();
  };

  /**
   * process batches that weren't auto-uploaded
   */

  const getOptions = () => {
    return clone(uploaderOptions);
  };
  const registerExtension = (name, methods) => {
    invariant(enhancerTime, EXT_OUTSIDE_ENHANCER_TIME);
    invariant(!extensions[name], EXT_ALREADY_EXISTS, name);
    logger.debugLog(`uploady.uploader: registering extension: ${name.toString()}`, methods);
    extensions[name] = methods;
  };
  let {
    trigger,
    target: uploader
  } = addLife({
    id: uploaderId,
    update,
    add,
    upload: uploadOptions => {
      processor.processPendingBatches(uploadOptions);
    },
    abort: id => {
      processor.abort(id);
    },
    abortBatch: id => {
      processor.abortBatch(id);
    },
    getOptions,
    clearPending,
    registerExtension,
    getExtension: name => {
      return extensions[name];
    }
  }, EVENT_NAMES, {
    canAddEvents: false,
    canRemoveEvents: false
  });

  /**
   * ensures that data being exposed to client-land isnt a proxy, only pojos
   */
  const triggerWithUnwrap = function (name) {
    for (var _len = arguments.length, data = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      data[_key - 1] = arguments[_key];
    }
    //delays unwrap to the very last time on trigger. Will only unwrap if there are listeners
    const lp = createLifePack(() => data.map(deepProxyUnwrap));
    return trigger(name, lp);
  };
  const cancellable = triggerCancellable(triggerWithUnwrap);
  const enhancedUploader = getEnhancedUploader(uploader, uploaderOptions, triggerWithUnwrap, state => {
    enhancerTime = state;
  });
  const processor = getProcessor(triggerWithUnwrap, cancellable, uploaderOptions, enhancedUploader.id);
  return devFreeze(enhancedUploader);
};
export default createUploader;